/*
 * Scroll-reveal helper shared by <HReveal> and <Reveal>: no animation library, one IntersectionObserver per
 * margin, and the style is flipped directly on the element (no React state, no re-render).
 */
export type RevealCfg = { duration: number; delay: number };

const EASE = "cubic-bezier(0.22,1,0.36,1)";
const pending = new WeakMap<Element, RevealCfg>();
const observers = new Map<number, IntersectionObserver>();

export function showReveal(el: HTMLElement, { duration, delay }: RevealCfg) {
  el.style.transition = `opacity ${duration}s ${EASE} ${delay}s, transform ${duration}s ${EASE} ${delay}s`;
  el.style.opacity = "1";
  el.style.transform = "none";
}

function observerFor(margin: number) {
  let io = observers.get(margin);
  if (!io) {
    io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const cfg = pending.get(e.target);
          if (cfg) showReveal(e.target as HTMLElement, cfg);
          pending.delete(e.target);
          io!.unobserve(e.target);
        }
      },
      { rootMargin: `0px 0px -${margin}px 0px` },
    );
    observers.set(margin, io);
  }
  return io;
}

/** Reveal `el` once it scrolls into view. Returns the cleanup function. */
export function observeReveal(el: HTMLElement, cfg: RevealCfg, margin = 60) {
  const io = observerFor(margin);
  pending.set(el, cfg);
  io.observe(el);
  return () => {
    pending.delete(el);
    io.unobserve(el);
  };
}
